import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import CustomCursor from '../../components/CustomCursor';

const sections = [
  { id: 'overview', label: 'Overview' },
  { id: 'problem', label: 'Problem Statement' },
  { id: 'research', label: 'Research' },
  { id: 'solution', label: 'Solution Space' },
  { id: 'prototype', label: 'Prototype' },
  { id: 'design-system', label: 'Design System' },
  { id: 'components', label: 'Components' },
  { id: 'usability', label: 'Usability Testing' },
  { id: 'final-ui', label: 'Final UI' },
  { id: 'reflection', label: 'Reflection' },
];

function SideNav() {
  const [activeSection, setActiveSection] = useState('overview');

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY + 120;
      let current = 'overview';
      for (const section of sections) {
        const el = document.getElementById(section.id);
        if (el && el.offsetTop <= scrollY) {
          current = section.id;
        }
      }
      setActiveSection(current);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      window.scrollTo({ top: el.offsetTop - 80, behavior: 'smooth' });
    }
  };

  return (
    <nav className="hidden xl:flex flex-col gap-1 fixed left-8 top-1/2 -translate-y-1/2 z-40 w-[160px]">
      {sections.map((s) => (
        <button
          key={s.id}
          onClick={() => scrollTo(s.id)}
          className={`text-left text-[13px] px-3 py-1.5 rounded-md transition-all cursor-pointer whitespace-nowrap ${
            activeSection === s.id
              ? 'text-notion-accent font-medium bg-notion-accent/10'
              : 'text-notion-secondary hover:text-notion-text hover:bg-notion-hover'
          }`}
        >
          {s.label}
        </button>
      ))}
    </nav>
  );
}

export default function RekindlePage() {
  const heroRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start end", "end start"]
  });
  
  const scale = useTransform(scrollYProgress, [0, 0.5], [0.95, 1]);
  const opacity = useTransform(scrollYProgress, [0, 0.3], [0.8, 1]);
  const y = useTransform(scrollYProgress, [0, 0.5], [30, 0]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans cursor-none">
      <CustomCursor />
      <SideNav />

      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-notion-border">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-4">
          <div className="flex items-center justify-between">
            <Link
              to="/"
              className="flex items-center gap-2 text-[14px] text-notion-secondary hover:text-notion-text transition-colors cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-left-line"></i>
              </span>
              <span>Back to Portfolio</span>
            </Link>
            <Link
              to="/"
              className="font-serif text-[18px] font-medium text-notion-text hover:text-notion-accent transition-colors cursor-pointer"
            >
              annie
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-[900px] mx-auto px-6 lg:px-0">
        {/* Hero / Overview */}
        <section id="overview" className="pt-12 lg:pt-16 mb-16">
          <h1 className="font-serif text-4xl lg:text-5xl font-medium text-notion-text leading-tight tracking-tight mb-6">
            Rekindle
          </h1>
          <p className="text-[18px] text-notion-secondary leading-relaxed mb-10">
            A supportive social space for rebuilding your emotional wellbeing one step at a time.
          </p>

          <div className="flex items-center gap-2 mb-10">
            <span className="text-[12px] text-notion-secondary bg-notion-tag px-2 py-1 rounded">
              DES 112
            </span>
            <span className="text-[12px] text-notion-secondary bg-notion-tag px-2 py-1 rounded">
              Annie Ding
            </span>
          </div>

          {/* Hero Image with Animation */}
          <motion.div
            ref={heroRef}
            style={{ scale, opacity, y }}
            className="w-full rounded-lg overflow-hidden bg-white mb-12"
          >
            <div className="w-full flex items-center justify-center">
              <img
                src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/abd11443fa097552af62217463df360c.png"
                alt="Rekindle App Hero"
                title="Rekindle Mental Wellness App - Annie Ding Portfolio"
                className="w-full h-auto object-contain"
              />
            </div>
          </motion.div>

          {/* Tools & Methodologies */}
          <div className="bg-notion-sidebar border border-notion-border rounded-lg p-8">
            <h3 className="text-[14px] font-medium text-notion-secondary uppercase tracking-wide mb-5">
              Tools Used &amp; Key Methodologies
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-[13px] font-medium text-notion-text mb-2">Tools</h4>
                <p className="text-[15px] text-notion-secondary">Figma, FigJam</p>
              </div>
              <div>
                <h4 className="text-[13px] font-medium text-notion-text mb-2">Methodologies</h4>
                <p className="text-[15px] text-notion-secondary">Design Thinking, Double Diamond Framework, Usability Testing, Paper Prototyping</p>
              </div>
            </div>
          </div>
        </section>

        {/* Brief */}
        <section className="mb-16">
          <h2 className="font-serif text-3xl font-medium text-notion-text mb-6">
            Brief
          </h2>
          <p className="text-[16px] text-notion-secondary leading-relaxed">
            Many people recover from toxic relationships struggle to rebuild their social confidence. Existing social media platforms can feel unsafe, lack support especially during the vulnerable healing periods they need.
          </p>
        </section>

        {/* Affinity Map */}
        <section className="mb-20">
          <h2 className="font-serif text-2xl font-medium text-notion-text mb-8">Affinity Map</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              'Fear of judgement',
              'Need for anonymity',
              'Seeking validation',
              'Isolation feelings',
              'Trust issues',
              'Want community',
              'Need resources',
              'Gradual healing',
              'Safe space'
            ].map((item, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 text-center">
                <p className="text-sm text-gray-700">{item}</p>
              </div>
            ))}
          </div>
        </section>

        {/* First Diamond - Problem Space */}
        <section className="mb-20">
          <h2 className="font-serif text-2xl font-medium text-notion-text mb-8">First Diamond - Problem Space</h2>
          <div className="bg-white rounded-lg overflow-hidden shadow-sm border border-gray-200 mb-8">
            <img 
              src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/1948c5ede8380daf88dfe3abc91615ad.png" 
              alt="First Diamond - Problem Space"
              className="w-full h-auto"
            />
          </div>
          <div className="border-l-4 border-notion-accent pl-6 py-4 bg-gray-50 rounded-r-lg">
            <p className="text-lg font-semibold text-gray-900">
              How might we help users rebuild a sense of safety and stability after so many years of uncertainty?
            </p>
          </div>
        </section>

        {/* User Persona */}
        <section className="mb-20">
          <h2 className="font-serif text-2xl font-medium text-notion-text mb-8">Research on Target Audience and Personas</h2>
          <div className="bg-white rounded-lg overflow-hidden shadow-sm border border-gray-200">
            <img 
              src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/7f10ce2bdf288d5355024ddb3748d53c.png" 
              alt="User Persona"
              className="w-full h-auto"
            />
          </div>
        </section>

        {/* Competitive Analysis */}
        <section className="mb-20">
          <h2 className="font-serif text-2xl font-medium text-notion-text mb-8">Competitive Analysis</h2>
          <div className="bg-white rounded-lg overflow-hidden shadow-sm border border-gray-200 mb-8">
            <img 
              src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/d67d755b8244c459cd7bb5d47d702e6f.png" 
              alt="Competitive Analysis"
              className="w-full h-auto"
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Oxfam America */}
            <div className="bg-white rounded-lg p-8 border border-gray-200">
              <h3 className="text-xl font-semibold mb-4 text-gray-900">Oxfam America</h3>
              
              <div className="mb-4">
                <h4 className="font-semibold text-gray-900 mb-2">Mission Statement:</h4>
                <p className="text-gray-700 text-sm">We are a global organization that fights inequality to end poverty and injustice.</p>
              </div>

              <div className="mb-4">
                <h4 className="font-semibold text-gray-900 mb-2">Target market:</h4>
                <p className="text-gray-700 text-sm">Socially conscious individuals aged 25–60, professionals who care about global inequality, poverty reduction</p>
              </div>

              <div className="mb-4">
                <h4 className="font-semibold text-gray-900 mb-2">Strengths:</h4>
                <ul className="list-disc list-inside text-gray-700 text-sm space-y-1">
                  <li>Strong global brand: recognized worldwide, builds instant trust</li>
                  <li>Clear messaging: "The future is equal" captures its mission and emotional appeal</li>
                  <li>Transparent Impact Reporting: Has clear stats like 15.25 million people helped to show their credibility</li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Weaknesses:</h4>
                <ul className="list-disc list-inside text-gray-700 text-sm space-y-1">
                  <li>Overloaded Homepage: There is so many call to actions its hard to navigate easily</li>
                  <li>US-Centric: Its messaging is primarily only US statistics but the goal is to help everyone globally</li>
                  <li>Limited Interactive features: It relies on a lot of static text instead of dynamic visuals</li>
                </ul>
              </div>
            </div>

            {/* OPAD */}
            <div className="bg-white rounded-lg p-8 border border-gray-200">
              <h3 className="text-xl font-semibold mb-4 text-gray-900">OPAD (Organization for poverty alleviation)</h3>
              
              <div className="mb-4">
                <h4 className="font-semibold text-gray-900 mb-2">Mission Statement:</h4>
                <p className="text-gray-700 text-sm">We at OPAD want to see an equitable world free from human suffering.</p>
              </div>

              <div className="mb-4">
                <h4 className="font-semibold text-gray-900 mb-2">Target market:</h4>
                <p className="text-gray-700 text-sm">Globally minded donors aged 30–65 interested in poverty alleviation especially in Africa/developing nations</p>
              </div>

              <div className="mb-4">
                <h4 className="font-semibold text-gray-900 mb-2">Strengths:</h4>
                <ul className="list-disc list-inside text-gray-700 text-sm space-y-1">
                  <li>Clear humanitarian focus</li>
                  <li>Emphasizes child welfare, youth empowerment and climate change</li>
                  <li>Community driven approach</li>
                  <li>References UN's consultative status</li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Weaknesses:</h4>
                <ul className="list-disc list-inside text-gray-700 text-sm space-y-1">
                  <li>Cluttered design</li>
                  <li>Mobile not optimized</li>
                  <li>Unclear navigation</li>
                  <li>Too many colors and overlays</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Second Diamond - Solution Space */}
        <section id="solution" className="mb-20">
          <h2 className="font-serif text-2xl font-medium text-notion-text mb-8">Second Diamond - Solution Space</h2>
          <p className="text-[16px] text-notion-secondary leading-relaxed mb-8">
            With our research insights finalized, we moved into designing a solution that balances safety with connection, professional support and a peer community that has the ability to keep the user anonymized and free for them to learn/post.
          </p>

          {/* Design Process Image */}
          <div className="bg-white rounded-lg overflow-hidden shadow-sm border border-gray-200 mb-8">
            <img 
              src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/f023a84616181c4f3df9135ca9cf9115.png" 
              alt="Design Process - Key User Flows"
              className="w-full h-auto"
            />
          </div>

          {/* User Flow Description */}
          <p className="text-[16px] text-notion-secondary leading-relaxed mb-8">
            Our key user flow was allowing our users to join a community group and start posting/engaging.
          </p>

          {/* Horizontal Flowchart */}
          <div className="flex items-center justify-between gap-2 bg-notion-sidebar border border-notion-border rounded-lg p-6 overflow-x-auto">
            <div className="flex items-center gap-3 min-w-max">
              <div className="bg-white border border-gray-200 rounded-lg px-4 py-3 text-center">
                <p className="text-sm text-gray-700 whitespace-nowrap">Launch App</p>
              </div>
              <span className="text-gray-400">→</span>
              <div className="bg-white border border-gray-200 rounded-lg px-4 py-3 text-center">
                <p className="text-sm text-gray-700 whitespace-nowrap">View Dashboard</p>
              </div>
              <span className="text-gray-400">→</span>
              <div className="bg-white border border-gray-200 rounded-lg px-4 py-3 text-center">
                <p className="text-sm text-gray-700 whitespace-nowrap">Browse Community</p>
              </div>
              <span className="text-gray-400">→</span>
              <div className="bg-white border border-gray-200 rounded-lg px-4 py-3 text-center">
                <p className="text-sm text-gray-700 whitespace-nowrap">Create Post</p>
              </div>
              <span className="text-gray-400">→</span>
              <div className="bg-white border border-gray-200 rounded-lg px-4 py-3 text-center">
                <p className="text-sm text-gray-700 whitespace-nowrap">Post Shared</p>
              </div>
            </div>
          </div>
        </section>

        {/* Prototype */}
        <section id="prototype" className="mb-16">
          <h2 className="font-serif text-2xl font-medium text-notion-text mb-6">
            Key Features to Prototype
          </h2>
          
          {/* 3 Feature Boxes */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-[16px] font-medium text-notion-text mb-3">Join a Group and Post</h3>
              <p className="text-[14px] text-notion-secondary leading-relaxed">
                Users will be able to browse recommended posts based on what groups they have joined, what posts they have interacted with, and share their own stories anonymously. The interface wants to encourage connection with the pressure of.
              </p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-[16px] font-medium text-notion-text mb-3">Access Help Line</h3>
              <p className="text-[14px] text-notion-secondary leading-relaxed">
                There will always be immediate access to crisis support which is available 24/7 from any screen. The help line is a necessary feature so that there is a quick connection to trained professionals at anytime during difficult moments.
              </p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-[16px] font-medium text-notion-text mb-3">Browse Recommended Resources</h3>
              <p className="text-[14px] text-notion-secondary leading-relaxed">
                There will be curated articles, videos, or exercises that help a user get through a difficult challenge they are currently going through based on what information the app has collected so they can find exactly what they need when they need it.
              </p>
            </div>
          </div>

          <h2 className="font-serif text-2xl font-medium text-notion-text mb-6">
            Initial Paper Prototype
          </h2>
          <div className="flex flex-col md:flex-row gap-6 mb-8">
            <div className="w-full md:w-1/2 rounded-lg overflow-hidden bg-white border border-gray-200">
              <img 
                src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/b3ab5140c5d9876c25a74b7726ddef81.png" 
                alt="Initial Paper Prototype"
                className="w-full h-auto"
              />
            </div>
            <div className="w-full md:w-1/2 bg-notion-tag border border-notion-border rounded-lg p-6 flex flex-col justify-center">
              <p className="text-[15px] text-notion-secondary leading-relaxed mb-3">
                I wanted to explore what a social feed can start to look like without making it so overwhelming like the modern ones we have today.
              </p>
              <p className="text-[15px] text-notion-secondary leading-relaxed">
                A finding in visibility was noticing the help button and finding out what types of colors we should go with.
              </p>
            </div>
          </div>
        </section>

        {/* Design System */}
        <section id="design-system" className="mb-16">
          <h2 className="font-serif text-2xl font-medium text-notion-text mb-6">
            Design System
          </h2>

          {/* Color Scheme */}
          <h3 className="font-serif text-xl font-medium text-notion-text mb-6">
            01. Color Scheme
          </h3>

          <div className="space-y-8 mb-12">
            <div className="w-full rounded-lg overflow-hidden bg-white">
              <img 
                src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/b9021c87849cbd8c5eec3cf445506473.png" 
                alt="Rekindle Color Scheme"
                className="w-full h-auto"
              />
            </div>

            <div className="bg-notion-tag border border-notion-border rounded-lg p-6">
              <p className="text-[15px] text-notion-secondary leading-relaxed">
                Rekindle gives the imagery of flames or an ability to reignite something that has been through a lot before so I wanted to use a reddish tone as one of the main colors, but still keep it easy on the eyes. So there&apos;s only 1 core color and greys.
              </p>
            </div>
          </div>

          {/* Typography */}
          <h3 className="font-serif text-xl font-medium text-notion-text mb-6">
            02. Typography
          </h3>

          <div className="space-y-6 mb-12">
            <div className="w-full rounded-lg overflow-hidden bg-white">
              <img 
                src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/ef29e95142ac76a7bba10e13b98153cf.png" 
                alt="Rekindle Typography"
                className="w-full h-auto"
              />
            </div>

            <div className="bg-notion-tag border border-notion-border rounded-lg p-6">
              <p className="text-[15px] text-notion-secondary leading-relaxed">
                I used Quicksand because of its rounded edges. It feels very soft and communicative towards people who need support. I went with 5 basic sizes that I believe can be useful for all cases.
              </p>
            </div>
          </div>

          {/* Iconography */}
          <h3 className="font-serif text-xl font-medium text-notion-text mb-6">
            03. Iconography
          </h3>

          <div className="space-y-6 mb-8">
            <div className="w-full rounded-lg overflow-hidden bg-white">
              <img 
                src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/1da9063dfbc8057f62e9440495a74cae.png" 
                alt="Rekindle Iconography"
                className="w-full h-auto"
              />
            </div>

            <div className="bg-notion-tag border border-notion-border rounded-lg p-6">
              <p className="text-[15px] text-notion-secondary leading-relaxed">
                I chose these main 4 icons because this would be the main bottom navigation bar. I believe I will add more as time goes on, but for now, these icons serve as the main functions of what we offer on the app.
              </p>
            </div>
          </div>

          {/* Imagery */}
          <h3 className="font-serif text-xl font-medium text-notion-text mb-6">
            04. Imagery
          </h3>

          <div className="space-y-6 mb-12">
            <div className="w-full rounded-lg overflow-hidden bg-white">
              <img 
                src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/d39611e6549f4306cc8b19a755227c72.png" 
                alt="Rekindle Imagery"
                className="w-full h-auto"
              />
            </div>

            <div className="bg-notion-tag border border-notion-border rounded-lg p-6">
              <p className="text-[15px] text-notion-secondary leading-relaxed">
                The goal in this imagery is to support emotional wellbeing. The goal is to make resource browsing deeply integrated with community interaction rather than a passive reading experience. Rekindle can cater and recommended you resources based on what you post and the groups you interact with.
              </p>
            </div>
          </div>
        </section>

        {/* 05. Initial Components */}
        <section id="components" className="mb-16">
          <h3 className="font-serif text-xl font-medium text-notion-text mb-6">
            05. Initial Components
          </h3>

          <div className="space-y-6 mb-8">
            <div className="w-full rounded-lg overflow-hidden bg-white">
              <img 
                src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/4f401857e1e518d1af96b18b7526effc.png" 
                alt="Rekindle Initial Components"
                className="w-full h-auto"
              />
            </div>

            <div className="bg-notion-tag border border-notion-border rounded-lg p-6">
              <p className="text-[15px] text-notion-secondary leading-relaxed">
                Here are some initial component designs. The buttons will be useful for mobile nav especially the + can be a floating icon at the bottom right. The text input fields will also be relevant for us as we will have people posting on their groups.
              </p>
            </div>
          </div>

          {/* Link to Last Prototype */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-6 hover:shadow-md transition-shadow">
            <a 
              href="https://www.figma.com/design/Wxv0zluxQCqhZG0ibGYxkZ/Final-Des-112-App-Project?node-id=0-1"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between group cursor-pointer"
            >
              <div>
                <h4 className="text-[16px] font-medium text-notion-text group-hover:text-purple-600 transition-colors mb-1">
                  Last Prototype for User Testing
                </h4>
                <p className="text-[13px] text-notion-secondary">View on Figma</p>
              </div>
              <span className="w-5 h-5 flex items-center justify-center text-purple-400 group-hover:text-purple-600 transition-colors">
                <i className="ri-external-link-line"></i>
              </span>
            </a>
          </div>
        </section>

        {/* Usability Testing */}
        <section id="usability" className="mb-16">
          <h2 className="font-serif text-2xl font-medium text-notion-text mb-8">
            Usability Testing
          </h2>

          <div className="space-y-8">
            {/* Usability Testing Image */}
            <div className="w-full rounded-lg overflow-hidden bg-white">
              <img 
                src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/f3082be11b0e33955c465fa560d50caf.png"
                alt="Usability Testing"
                className="w-full h-auto"
              />
            </div>

            {/* Findings */}
            <div className="space-y-6">
              {/* Finding 1 */}
              <div className="bg-white border border-notion-border rounded-lg p-6">
                <h4 className="text-[15px] font-semibold text-notion-text mb-3">
                  1) Users felt the Home page had too many actions and overlapping functions with the nav bar.
                </h4>
                <ul className="space-y-2 mb-3">
                  <li className="text-[14px] text-notion-secondary flex items-start gap-2">
                    <span className="text-notion-accent mt-1">•</span>
                    <span>Remove or simplify the "Quick Actions" module</span>
                  </li>
                  <li className="text-[14px] text-notion-secondary flex items-start gap-2">
                    <span className="text-notion-accent mt-1">•</span>
                    <span>Prioritize one primary CTA on the home screen</span>
                  </li>
                  <li className="text-[14px] text-notion-secondary flex items-start gap-2">
                    <span className="text-notion-accent mt-1">•</span>
                    <span>Let the secondary CTA's live in navigation menus or others only</span>
                  </li>
                </ul>
                <p className="text-[14px] text-notion-text font-medium">
                  Expected Impact: Reduces a lot of the cognitive load and aligns the home page more cleanly & clearly.
                </p>
              </div>

              {/* Finding 2 */}
              <div className="bg-white border border-notion-border rounded-lg p-6">
                <h4 className="text-[15px] font-semibold text-notion-text mb-3">
                  2) Post categorization isn't clear
                </h4>
                <ul className="space-y-2 mb-3">
                  <li className="text-[14px] text-notion-secondary flex items-start gap-2">
                    <span className="text-notion-accent mt-1">•</span>
                    <span>Add more category icon tags like "advice", "venting", etc.</span>
                  </li>
                </ul>
                <p className="text-[14px] text-notion-text font-medium">
                  Expected Impact: Helps users scan content faster and skip ones they don't want to see.
                </p>
              </div>

              {/* Finding 3 */}
              <div className="bg-white border border-notion-border rounded-lg p-6">
                <h4 className="text-[15px] font-semibold text-notion-text mb-3">
                  3) Resource Recommendation Logic Not Explained
                </h4>
                <ul className="space-y-2 mb-3">
                  <li className="text-[14px] text-notion-secondary flex items-start gap-2">
                    <span className="text-notion-accent mt-1">•</span>
                    <span>Add a 1-2 sentence description on why these specific resources were catered towards them, maybe a tooltip on why they are seeing this one.</span>
                  </li>
                </ul>
                <p className="text-[14px] text-notion-text font-medium">
                  Expected Impact: Builds more trust and transparency/personalization with the user.
                </p>
              </div>
            </div>

            {/* Link to Final Prototype */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-6 hover:shadow-md transition-shadow">
              <a 
                href="https://www.figma.com/design/Wxv0zluxQCqhZG0ibGYxkZ/Final-Des-112-App-Project?node-id=36-2"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between group cursor-pointer"
              >
                <div>
                  <h4 className="text-[16px] font-medium text-notion-text group-hover:text-purple-600 transition-colors mb-1">
                    Final Prototype
                  </h4>
                  <p className="text-[13px] text-notion-secondary">View on Figma</p>
                </div>
                <span className="w-5 h-5 flex items-center justify-center text-purple-400 group-hover:text-purple-600 transition-colors">
                  <i className="ri-external-link-line"></i>
                </span>
              </a>
            </div>
          </div>
        </section>

        {/* Final UI Screens */}
        <section id="final-ui" className="mb-16">
          <h2 className="font-serif text-2xl font-medium text-notion-text mb-8">
            Final UI Screens
          </h2>
          
          {/* Onboarding */}
          <h3 className="font-serif text-xl font-medium text-notion-text mb-6">
            Onboarding
          </h3>
          <div className="w-full rounded-lg overflow-hidden bg-white mb-6">
            <img 
              src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/78a031b4fae1d3783892104cdfd471f2.png"
              alt="Rekindle Onboarding Screens"
              className="w-full h-auto"
            />
          </div>
          <div className="w-full rounded-lg overflow-hidden bg-white mb-12">
            <img 
              src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/9a654540c04b8d929ead4efa33153406.png"
              alt="Rekindle Onboarding Path Selection Screens"
              className="w-full h-auto"
            />
          </div>

          {/* Task 1 */}
          <h3 className="font-serif text-xl font-medium text-notion-text mb-6">
            Task 1 - Creating a new post in a group
          </h3>
          <div className="w-full rounded-lg overflow-hidden bg-white mb-6">
            <img 
              src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/7eadf209e270b24a52c4e53facda3eb4.png"
              alt="Task 1 - Creating a new post in a group"
              className="w-full h-auto"
            />
          </div>
          <div className="w-full rounded-lg overflow-hidden bg-white mb-12">
            <img 
              src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/5ab9cfc83c58cc517613e21eaaa2b66d.png"
              alt="Task 1 - Post creation flow continued"
              className="w-full h-auto"
            />
          </div>

          {/* Task 2 */}
          <h3 className="font-serif text-xl font-medium text-notion-text mb-6">
            Task 2 - Joining a recommended group
          </h3>
          <div className="w-full rounded-lg overflow-hidden bg-white mb-12">
            <img 
              src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/99349446bb4ba253135624b32c55b1a2.png"
              alt="Task 2 - Joining a recommended group"
              className="w-full h-auto"
            />
          </div>

          {/* Task 3 */}
          <h3 className="font-serif text-xl font-medium text-notion-text mb-6">
            Task 3 - Visiting the resources page, help line, and profile
          </h3>
          <div className="w-full rounded-lg overflow-hidden bg-white mb-6">
            <img 
              src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/fdf9d04408315c5be585e6c8451649e5.png"
              alt="Task 3 - Visiting the resources page, help line, and profile"
              className="w-full h-auto"
            />
          </div>
          <div className="w-full rounded-lg overflow-hidden bg-white mb-12">
            <img 
              src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/dabfee754fed63b6a4d073b1067d7cd5.png"
              alt="Task 3 - Resources, help line, and profile screens continued"
              className="w-full h-auto"
            />
          </div>
          <div className="w-full rounded-lg overflow-hidden bg-white mb-12">
            <img 
              src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/55f1597b95d3c205ee8350ae99ee6971.png"
              alt="Task 3 - Additional profile and settings screens"
              className="w-full h-auto"
            />
          </div>
        </section>

        {/* Reflection and Next Steps */}
        <section id="reflection" className="mb-16">
          <h2 className="font-serif text-2xl font-medium text-notion-text mb-8">
            Reflection and Next Steps
          </h2>

          <div className="space-y-8">
            {/* What makes my product feasible, viable, and desirable? */}
            <div>
              <h3 className="text-[18px] font-semibold text-notion-text mb-4">
                What makes my product feasible, viable, and desirable?
              </h3>
              <div className="space-y-4">
                <div>
                  <h4 className="text-[15px] font-medium text-notion-text mb-2">Feasibility:</h4>
                  <p className="text-[15px] text-notion-secondary leading-relaxed">
                    The core features are achievable with what is currently existing in mobile development frameworks, specifically with the catered recommendations based on previously engaged with content. Crisis support integration require partnerships but are well-established services.
                  </p>
                </div>
                <div>
                  <h4 className="text-[15px] font-medium text-notion-text mb-2">Viability:</h4>
                  <p className="text-[15px] text-notion-secondary leading-relaxed">
                    Mental wellness is a growing market especially within the Gen-Z space. Revenue could come from premium features, therapy partnerships, and different institutional licensing. There could be b2b possibilities for support organization/personal custom groups.
                  </p>
                </div>
                <div>
                  <h4 className="text-[15px] font-medium text-notion-text mb-2">Desirability:</h4>
                  <p className="text-[15px] text-notion-secondary leading-relaxed">
                    User testing showed that the color scheme and concept had strong emotional connection. The gap in the market is clear especially when looking at competitors. Users expressed willingness and would recommend the app. A lot of users really enjoyed the possibility of having personally catered content.
                  </p>
                </div>
              </div>
            </div>

            {/* What did I learn? */}
            <div>
              <h3 className="text-[18px] font-semibold text-notion-text mb-4">
                What did I learn?
              </h3>
              <div className="space-y-3">
                <p className="text-[15px] text-notion-secondary leading-relaxed">
                  Designing for vulnerable users is difficult. It requires a lot more sensitivity and balance compared to creating something within SaaS. A lot of building of trust is required.
                </p>
                <p className="text-[15px] text-notion-secondary leading-relaxed">
                  User testing is also a lot more helpful than I realized especially with assumptions that come with mental health and social media in our current society.
                </p>
                <p className="text-[15px] text-notion-secondary leading-relaxed">
                  Finally, visual design is not about aesthetics, the color scheme and every decision is made with intent and feedback.
                </p>
              </div>
            </div>

            {/* How would I make this project real? */}
            <div>
              <h3 className="text-[18px] font-semibold text-notion-text mb-4">
                How would I make this project real?
              </h3>
              <div className="space-y-3">
                <p className="text-[15px] text-notion-secondary leading-relaxed">
                  I would try to attempt to build the back-end of the catering/recommendations of the groups and get at least 5 people to try using it. Once I have validated that the 5 people enjoy the app, I would reach out to volunteer groups or crisis organizations. I would keep trying to market myself to them until we get a partnership. Along the way, we should continue to iterate on the idea and take feedback.
                </p>
                <p className="text-[15px] text-notion-secondary leading-relaxed">
                  I would build in phases but keep things fast. The most important factor would be sales and marketing and getting users to truly like it enough to recommend the product.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Footer */}
      <footer className="border-t border-notion-border bg-notion-sidebar">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <Link
                to="/"
                className="font-serif text-[20px] font-medium text-notion-text hover:text-notion-accent transition-colors cursor-pointer"
              >
                annie
              </Link>
              <p className="text-[13px] text-notion-secondary mt-2">
                Product Designer &amp; UX Strategist
              </p>
            </div>
            <div className="flex items-center gap-6">
              <a
                href="mailto:ading@ucdavis.edu"
                className="text-[13px] text-notion-secondary hover:text-notion-accent transition-colors cursor-pointer"
              >
                ading@ucdavis.edu
              </a>
              <a
                href="https://www.linkedin.com/in/annieding04/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[13px] text-notion-secondary hover:text-notion-accent transition-colors cursor-pointer"
              >
                LinkedIn
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
